import { Coordinates, PrayerTimes as AdhanTimes } from "adhan";
import { getParams, imsakFrom, fmtTime } from "@/lib/prayerTimes";

// Fasting mode: Imsak & sahur reminders that follow the shared location schedule.
const KEY = "fasting_reminder";
export const FASTING_SCHEDULES = ["daily", "monThu", "ayyamulBidh", "ramadan"];
export const FASTING_LEADS = [30, 45, 60, 90];
export const IFTAR_LEADS = [5, 10, 15, 30];
const DEFAULT = { on: false, schedule: "ramadan", lead: 60, iftarLead: 15 };

// Du'a when breaking the fast — HR. Abu Dawud 2357 (hasan).
export const IFTAR_DUA = {
  arabic: "ذَهَبَ الظَّمَأُ وَابْتَلَّتِ الْعُرُوقُ وَثَبَتَ الْأَجْرُ إِنْ شَاءَ اللهُ",
  latin: "Dzahabazh-zhama'u wabtallatil-'uruqu wa tsabatal-ajru insya Allah",
  meaning: { id: "Telah hilang dahaga, telah basah urat-urat, dan telah tetap pahala, insya Allah.", en: "The thirst is gone, the veins are moistened, and the reward is confirmed, if Allah wills.", ar: "ذهب العطش، وابتلّت العروق، وثبت الأجر إن شاء الله." },
  source: { id: "HR. Abu Dawud no. 2357 — hasan (al-Albani). Dibaca setelah berbuka (meneguk minuman pertama); sebelumnya ucapkan bismillah.", en: "Abu Dawud no. 2357 — hasan (al-Albani). Said after breaking the fast (first sip); say bismillah before.", ar: "رواه أبو داود (2357) — حسنه الألباني. يُقال بعد الإفطار (أول شربة)؛ وقبله التسمية." },
};

export function readFasting() {
  try {
    const v = { ...DEFAULT, ...JSON.parse(localStorage.getItem(KEY) || "{}") };
    if (!FASTING_SCHEDULES.includes(v.schedule)) v.schedule = DEFAULT.schedule;
    if (!FASTING_LEADS.includes(Number(v.lead))) v.lead = DEFAULT.lead;
    if (!IFTAR_LEADS.includes(Number(v.iftarLead))) v.iftarLead = DEFAULT.iftarLead;
    return { on: !!v.on, schedule: v.schedule, lead: Number(v.lead), iftarLead: Number(v.iftarLead) };
  } catch { return { ...DEFAULT }; }
}
export function writeFasting(v) { localStorage.setItem(KEY, JSON.stringify(v)); return v; }
// For code outside PrayerScheduleContext (the app-wide prayer pop-up): is fasting mode on for today?
export function isFastingToday(date = new Date()) {
  const f = readFasting();
  return f.on && isFastingDay(date, f.schedule);
}

const hijriFmt = (() => { try { return new Intl.DateTimeFormat("en-u-ca-islamic-umalqura", { day: "numeric", month: "numeric" }); } catch { return null; } })();
// Hijri {day, month} of a Gregorian calendar date (Umm al-Qura, daytime mapping — the fast of Hijri 13 is the daytime
// of the Gregorian date that maps to 13, and its sahur falls in that same morning).
export function hijriOf(date) {
  if (!hijriFmt) return null;
  const parts = hijriFmt.formatToParts(date);
  const get = (t) => Number(parts.find((p) => p.type === t)?.value);
  return { day: get("day"), month: get("month") };
}

export function isFastingDay(date, schedule) {
  if (schedule === "daily") return true;
  if (schedule === "monThu") return date.getDay() === 1 || date.getDay() === 4;
  const h = hijriOf(date);
  if (!h) return false;
  if (schedule === "ayyamulBidh") return h.day >= 13 && h.day <= 15;
  if (schedule === "ramadan") return h.month === 9;
  return false;
}

// Next fasting day (today included) within `horizon` days, or null.
export function nextFastingDay(from, schedule, horizon = 400) {
  for (let i = 0; i < horizon; i++) {
    const d = new Date(from.getFullYear(), from.getMonth(), from.getDate() + i);
    if (isFastingDay(d, schedule)) return d;
  }
  return null;
}

// Push payload for the next 7 days on every fasting day: "sahur" (lead min before Imsak), "Imsak",
// "menjelang berbuka" (iftarLead min before Maghrib) and "berbuka" (Maghrib, with the iftar du'a).
export function buildFastingReminders({ here, today, resolvedMethod, fasting, lang, tz, labels }) {
  const c = new Coordinates(here[0], here[1]);
  const items = [];
  const nowMs = Date.now();
  for (let i = 0; i <= 7; i++) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
    if (!isFastingDay(d, fasting.schedule)) continue;
    const pt = new AdhanTimes(c, d, getParams(resolvedMethod));
    const imsak = imsakFrom(pt.fajr);
    const imsakStr = fmtTime(imsak, lang, tz);
    const fajrStr = fmtTime(pt.fajr, lang, tz);
    const sahurAt = imsak.getTime() - fasting.lead * 60000;
    if (sahurAt > nowMs) items.push({ send_at: new Date(sahurAt).toISOString(), title: labels.sahurTitle, body: labels.sahurBody(fasting.lead, imsakStr), url: "/times" });
    if (imsak.getTime() > nowMs) items.push({ send_at: imsak.toISOString(), title: labels.imsakTitle, body: labels.imsakBody(fajrStr), url: "/times" });
    const maghribStr = fmtTime(pt.maghrib, lang, tz);
    const soonAt = pt.maghrib.getTime() - fasting.iftarLead * 60000;
    if (soonAt > nowMs) items.push({ send_at: new Date(soonAt).toISOString(), title: labels.iftarSoonTitle, body: labels.iftarSoonBody(fasting.iftarLead, maghribStr), url: "/times" });
    if (pt.maghrib.getTime() > nowMs) items.push({ send_at: pt.maghrib.toISOString(), title: labels.iftarTitle, body: labels.iftarBody(maghribStr), url: "/times" });
  }
  return items;
}
